import React from 'react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import IconImage from '@/assets/icons/icon-image.svg?react';
import IconMagnifyingGlassPlus from '@/assets/icons/icon-magnifying-glass-plus.svg?react';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import IconText from '@/assets/icons/icon-text.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { useEditor } from '@craftjs/core';
import { BUTTON_NODE_NAME, IMAGE_NODE_NAME, TEXT_NODE_NAME } from '@/utils/constants';
import type { InteractionEvent } from '@builder4/shared/types/craft';
import type { CustomText } from '@builder4/shared/types/text';
import { OPEN_POPUP_EVENT, NO_LINK_EVENT } from '@builder4/shared/utils/constants';
import { Text, type Descendant } from 'slate';
import { useScrollNodeIntoView } from '@/hooks/useScrollNodeIntoView';

const NODE_TYPE_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [BUTTON_NODE_NAME]: IconMoreHorizontalSquare,
  [IMAGE_NODE_NAME]: IconImage,
  [TEXT_NODE_NAME]: IconText,
};

type ConnectedElement = {
  id: string;
  name: string;
  typeName: string;
};

interface PopupClickTriggerPanelProps {
  popupId: string;
  description: string;
}

function hasPopupLeaf(descendants: Descendant[], popupId: string): boolean {
  for (const node of descendants) {
    if (Text.isText(node)) {
      const leaf = node as CustomText;
      if (
        leaf.clickEvent?.eventType === OPEN_POPUP_EVENT &&
        leaf.clickEvent?.eventData?.optionId === popupId
      ) {
        return true;
      }
    } else if ('children' in node) {
      if (hasPopupLeaf(node.children, popupId)) {
        return true;
      }
    }
  }
  return false;
}

function clearPopupLeaves(descendants: Descendant[], popupId: string): Descendant[] {
  return descendants.map((node) => {
    if (Text.isText(node)) {
      const leaf = node as CustomText;
      if (
        leaf.clickEvent?.eventType === OPEN_POPUP_EVENT &&
        leaf.clickEvent?.eventData?.optionId === popupId
      ) {
        return { ...leaf, clickEvent: undefined, selectedLinkVariant: undefined } as Descendant;
      }
      return node;
    } else if ('children' in node) {
      return { ...node, children: clearPopupLeaves(node.children, popupId) as CustomText[] };
    }
    return node;
  });
}

export const PopupClickTriggerPanel: React.FC<PopupClickTriggerPanelProps> = ({
  popupId,
  description,
}) => {
  const { query, actions, connectedElements } = useEditor((_, query) => {
    const allNodes = query.getNodes();
    const elements: ConnectedElement[] = [];
    for (const n of Object.values(allNodes)) {
      // Check component-level clickEvent (Button, Image, etc.)
      const clickEvent = n.data.props?.clickEvent as InteractionEvent | undefined;
      if (
        clickEvent?.eventType === OPEN_POPUP_EVENT &&
        clickEvent?.eventData?.optionId === popupId
      ) {
        elements.push({
          id: n.id,
          name: n.data.custom?.displayName || n.data.displayName,
          typeName: n.data.displayName,
        });
        continue;
      }

      // Check Text nodes' Slate leaves for popup click events
      if (n.data.displayName === TEXT_NODE_NAME) {
        const textStyles = n.data.props?.textStyles as Descendant[] | undefined;
        if (textStyles && hasPopupLeaf(textStyles, popupId)) {
          elements.push({
            id: n.id,
            name: n.data.custom?.displayName || n.data.displayName,
            typeName: n.data.displayName,
          });
        }
      }
    }
    return { connectedElements: elements };
  });

  const scrollNodeIntoView = useScrollNodeIntoView();

  const handleSelectElement = (elementId: string) => {
    actions.selectNode(elementId);
    scrollNodeIntoView(elementId);
  };

  const handleDisconnectElement = (elementId: string) => {
    const node = query.node(elementId).get();

    if (node.data.displayName === TEXT_NODE_NAME) {
      // For Text nodes, clear clickEvent and selectedLinkVariant from matching Slate leaves
      actions.setProp(elementId, (props: Record<string, unknown>) => {
        const textStyles = props.textStyles as Descendant[] | undefined;
        if (textStyles) {
          props.textStyles = clearPopupLeaves(textStyles, popupId);
        }
      });
    } else {
      actions.setProp(elementId, (props: Record<string, unknown>) => {
        const clickEvent = props.clickEvent as InteractionEvent | undefined;
        if (clickEvent?.eventData) {
          props.clickEvent = { eventType: NO_LINK_EVENT, eventData: undefined };
        }
      });
    }
  };

  return (
    <>
      <p className="text-xs text-neutral-500">{description}</p>
      <hr className="border-neutral-100" />
      <p className="text-xs font-semibold text-neutral-900">Triggered by</p>
      {connectedElements.length === 0 ? (
        <p className="text-xs text-neutral-500">
          This popup isn&apos;t connected to any button or element yet. Assign one using Actions.
          See the tutorial.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {connectedElements.map((element) => {
            const TypeIcon = NODE_TYPE_ICONS[element.typeName];
            return (
              <div key={element.id} className="flex items-center gap-1">
                <div className="flex h-8 flex-1 items-center gap-2 rounded-md bg-neutral-50 px-2">
                  {TypeIcon && <TypeIcon className="h-4 w-4 flex-shrink-0 text-neutral-500" />}
                  <span className="text-button-sm truncate font-medium text-neutral-900">
                    {element.name}
                  </span>
                </div>
                <IconSimpleButton
                  aria-label="Select element"
                  icon={IconMagnifyingGlassPlus}
                  onClick={() => handleSelectElement(element.id)}
                />
                <IconSimpleButton
                  aria-label="Disconnect element"
                  icon={IconDelete}
                  onClick={() => handleDisconnectElement(element.id)}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
